import { FC, useEffect, useCallback, useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { FieldValues, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { Buffer } from 'buffer';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';

import { isHusqvarna } from 'config';
import { loginRedirect } from 'services/authService';
import { authSelector } from 'store/authState';
import { productRegistrationSelector } from 'store/productRegistrationState/productRegistrationState';
import {
  signUp,
  fetchCaptcha,
  signUpSelector,
  submitCompleteProfile,
  resetCompleteProfile
} from 'store/signupState';
import { localizationSelector } from 'store/localizationState';
import * as routes from 'router/routes';
import { useTranslations } from 'hooks/useTranslations';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { pushGTMEvent } from 'utils/analyticsUtils';
import { showSuccessToast } from 'utils/toastUtils';
import { allKeysAreEmptyStrings } from 'utils/objectUtils';
import { getCustomerSignupProductMeta } from 'utils/customerUtils';
import { getBrowserCountryCode } from 'utils/localizationUtils';
import { CustomerType, DiamGroups } from 'models/customer';
import userIcon from 'assets/vectors/user.svg';

import Loader from 'components/UI/Loader';
import Button from 'components/UI/Buttons/Button';
import InputField from 'components/UI/InputField';
import SelectField from 'components/UI/SelectField';
import AnimatedFormGroup from 'components/UI/AnimatedFormGroup';
import PhoneNumberField from 'components/UI/PhoneNumberField';
import BackButton from 'components/UI/Buttons/BackButton';
import PageHeader from 'components/UI/Page/PageHeader';
import PageImageWrapper from 'components/UI/Page/PageImageWrapper';
import SeoHelmet from 'components/fragments/SeoHelmet';

import { Form, ButtonContainer } from '../styled';
import { CaptchaContainer, CaptchaCode, TwoColGrid } from './styled';

export const CompleteProfilePage: FC = () => {
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data, isLoading, isSuccess, isCredentialsCompleted, error } =
    useAppSelector(signUpSelector);
  const { session, hasLoggedIn } = useAppSelector(authSelector);
  const { productInfo } = useAppSelector(productRegistrationSelector);
  const { languageCode = 'en', countryOptions } =
    useAppSelector(localizationSelector);

  // Define Zod schema
  const schema = z
    .object({
      firstName: z.string().min(1, t('signup.form.firstname_required')),
      lastName: z.string().min(1, t('signup.form.lastname_required')),
      street: z.string().optional(),
      zipCode: z.string().optional(),
      state: z.string().optional(),
      city: z.string().optional(),
      country: z.string().min(1, t('signup.form.country_required')),
      dialCode: z.string().optional(),
      phoneNumber: z.string().optional(),
      captchaCode: z.string().min(1, t('signup.form.enter_captcha_prompt'))
    })
    .superRefine((data, ctx) => {
      if (data.street && !data.zipCode) {
        ctx.addIssue({
          path: ['zipCode'],
          code: 'custom',
          message: 'input.required'
        });
      }
      if (data.street && !data.city) {
        ctx.addIssue({
          path: ['city'],
          code: 'custom',
          message: 'input.required'
        });
      }
    });

  const {
    handleSubmit,
    register,
    formState: { errors },
    clearErrors,
    setValue,
    control
  } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      firstName: '',
      lastName: '',
      street: '',
      zipCode: '',
      city: '',
      country: '',
      state: '',
      dialCode: '',
      phoneNumber: '',
      captchaCode: ''
    },
    resolver: zodResolver(schema)
  });

  // State
  const { email, password, captchaElement, accountStatus } = data;

  // Watch - using useWatch for React 19 compiler compatibility
  const [watchCountry, watchStreet] = useWatch({
    control,
    name: ['country', 'street']
  });

  // Reset inputfield
  const reset = useCallback(
    (name: any) => {
      setValue(name, '');
      clearErrors(name);
    },
    [setValue, clearErrors]
  );

  // Fetch captcha
  useEffect(() => {
    dispatch(fetchCaptcha(email));
  }, [dispatch, email]);

  // Reset zip code and city
  useEffect(() => {
    if (!watchStreet) {
      setValue('zipCode', '');
      setValue('city', '');
      setValue('state', '');
      clearErrors(['zipCode', 'city', 'state']);
    }
  }, [setValue, clearErrors, watchStreet]);

  // Reset captcha code
  useEffect(() => {
    if (error) {
      dispatch(fetchCaptcha(email));
    }
    if (error?.code === 'captcha.not.found') {
      reset('captchaCode');
    }
  }, [dispatch, reset, email, error]);

  // Complete status
  useEffect(() => {
    if (!accountStatus.loginAccountExists) {
      return;
    }
    if (accountStatus.emailVerified && !session.authenticated) {
      loginRedirect(languageCode, email, true);
      return;
    }
  }, [t, accountStatus, email, languageCode, session]);

  // Submit
  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (values) => {
      pushGTMEvent('signupComplete', {
        customerType: CustomerType.IndividualCustomer
      });
      const newAddress = {
        street: values.street,
        zipCode: values.zipCode,
        city: values.city,
        state: values.state,
        country: ''
      };

      if (!allKeysAreEmptyStrings(newAddress)) {
        newAddress.country = values.country;
      }

      const { dialCode, phoneNumber } = values;

      dispatch(
        submitCompleteProfile({
          firstName: values.firstName,
          lastName: values.lastName,
          captchaCode: values.captchaCode,
          address: newAddress,
          country: values.country,
          language: languageCode,
          phoneNumber: {
            dialCode,
            number: phoneNumber
          }
        })
      );

      dispatch(
        signUp(
          {
            email,
            password,
            firstName: values.firstName,
            lastName: values.lastName,
            country: values.country,
            language: languageCode,
            type: CustomerType.IndividualCustomer,
            phoneNumber:
              dialCode && phoneNumber ? `${dialCode}${phoneNumber}` : '',
            locationAddress: newAddress,
            billingAddress: newAddress,
            shippingAddress: {},
            groups: [
              isHusqvarna ? DiamGroups.Husqvarna : DiamGroups.Construction
            ],
            captchaCode: values.captchaCode
          },
          {
            product: getCustomerSignupProductMeta(productInfo),
            consents: data.emailMarketing
              ? [
                  {
                    consentTypeId: data.consentType?.id ?? '',
                    status: 'ACCEPTED'
                  }
                ]
              : undefined
          }
        )
      );
    },
    [dispatch, data, productInfo, languageCode, email, password]
  );

  // Cancel company details
  const onBack = useCallback(() => {
    dispatch(resetCompleteProfile());
    navigate(routes.SIGNUP);
  }, [dispatch, navigate]);

  // On dial code change
  const onDialCodeChange = useCallback(
    (value: string) => setValue('dialCode', value),
    [setValue]
  );

  // Set default country code
  const defaultCountryCode = useMemo(() => getBrowserCountryCode(), []);

  // Show success toast and handle navigation
  useEffect(() => {
    if (isSuccess) {
      if (session.authenticated) {
        showSuccessToast(t('signup.success.account_created'));
      } else {
        showSuccessToast(t('signup.success.email_needs_verification'));
      }
    }
  }, [isSuccess, session.authenticated, t]);

  // Redirect to products if authenticated and account is created
  if (session.authenticated && isSuccess) {
    return <Navigate to={routes.PRODUCTS} />;
  }

  // Redirect to verify email if not authenticated and account is created
  if (!session.authenticated && isSuccess) {
    return (
      <Navigate
        to={`${routes.SIGNUP_VERIFY_EMAIL}?email=${encodeURIComponent(email)}`}
      />
    );
  }

  // Redirect to login if user has logged in but is not authenticated
  if (hasLoggedIn && !session.authenticated) {
    return <Navigate to={routes.LOGIN} />;
  }

  // Redirect if state is incorrect
  if (!isCredentialsCompleted) {
    return <Navigate to={routes.SIGNUP} />;
  }

  // Loading
  if (isLoading) {
    return <Loader center padding size="large" />;
  }

  return (
    <PageImageWrapper alignTop>
      <SeoHelmet title="signup.complete_profile.meta_title" />
      <BackButton onClick={onBack}>{t('productinfo.btn_back')}</BackButton>
      <PageHeader
        title={t('signup.complete_profile.title')}
        subtitle={t('signup.complete_profile.subtitle')}
        image={userIcon}
        imageAlt="user"
      />
      <Form onSubmit={handleSubmit(onSubmit)}>
        <TwoColGrid>
          <InputField
            name="firstName"
            description="signup.form.label_firstname"
            error={errors.firstName}
            register={register('firstName')}
            reset={reset}
          />
          <InputField
            name="lastName"
            description="signup.form.label_lastname"
            error={errors.lastName}
            register={register('lastName')}
            reset={reset}
          />
        </TwoColGrid>
        <SelectField
          name="country"
          register={register('country')}
          setValue={setValue}
          clearErrors={clearErrors}
          placeholder={t('signup.form.label_country_prompt')}
          description="signup.form.label_country"
          error={errors.country}
          defaultValue={defaultCountryCode}
          options={countryOptions}
        />
        <PhoneNumberField
          description="signup.form.label_phone_number"
          numberInputRef={register('phoneNumber')}
          dialCodeInputRef={register('dialCode')}
          onDialCodeChange={onDialCodeChange}
          defaultCountry={watchCountry}
          error={errors.phoneNumber}
        />
        <InputField
          name="street"
          register={register('street')}
          description="signup.form.label_address"
          error={errors.street}
          reset={reset}
        />
        <AnimatedFormGroup isOpen={!!watchStreet}>
          <InputField
            name="zipCode"
            register={register('zipCode')}
            description="signup.form.label_zipCode"
            error={errors.zipCode}
            reset={reset}
          />
          {watchCountry === 'US' && (
            <InputField
              name="state"
              register={register('state')}
              description="signup.form.label_state"
              error={errors.state}
              reset={reset}
            />
          )}
          <InputField
            name="city"
            register={register('city')}
            description="signup.form.label_city"
            error={errors.city}
            reset={reset}
          />
        </AnimatedFormGroup>
        {captchaElement && (
          <CaptchaContainer>
            <CaptchaCode
              src={`data:image/svg+xml;base64,${Buffer.from(
                captchaElement
              ).toString('base64')}`}
              alt="captcha"
            />
          </CaptchaContainer>
        )}
        <InputField
          name="captchaCode"
          description="signup.form.label_captcha_code"
          register={register('captchaCode')}
          error={errors.captchaCode}
          uppercase
        />
        <ButtonContainer>
          <Button submit fullWidth>
            {t('button.complete')}
          </Button>
          <Button
            onClick={onBack}
            color="background"
            textColor="primary"
            border
          >
            {t('button.back')}
          </Button>
        </ButtonContainer>
      </Form>
    </PageImageWrapper>
  );
};
